(() => {
  const SELECTORS = {
    item: '[data-qk-sound-test]',
    toggle: '[data-qk-sound-toggle]',
    slider: '[data-qk-sound-slider]',
    audio: '[data-qk-sound-audio]',
    current: '[data-qk-sound-current]',
    duration: '[data-qk-sound-duration]',
  };

  let activeInstance = null;

  function formatTime(timeInSeconds) {
    if (!Number.isFinite(timeInSeconds) || timeInSeconds < 0) {
      return '00:00';
    }

    const totalSeconds = Math.floor(timeInSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  class SoundTestTrack {
    constructor(element) {
      this.element = element;
      this.title = element.dataset.qkSoundTitle || 'sound test';
      this.toggle = element.querySelector(SELECTORS.toggle);
      this.slider = element.querySelector(SELECTORS.slider);
      this.audio = element.querySelector(SELECTORS.audio);
      this.currentTimeElement = element.querySelector(SELECTORS.current);
      this.durationElement = element.querySelector(SELECTORS.duration);

      if (!this.toggle || !this.slider || !this.audio || !this.currentTimeElement || !this.durationElement) {
        return;
      }

      this.element.dataset.qkSoundInitialized = 'true';
      this.registerEvents();
      this.updateUi();
    }

    registerEvents() {
      this.toggle.addEventListener('click', () => this.togglePlayback());
      this.slider.addEventListener('input', () => this.handleSliderInput());
      this.audio.addEventListener('loadedmetadata', () => this.updateUi());
      this.audio.addEventListener('durationchange', () => this.updateUi());
      this.audio.addEventListener('timeupdate', () => this.updateUi());
      this.audio.addEventListener('play', () => this.handlePlay());
      this.audio.addEventListener('pause', () => this.handlePause());
      this.audio.addEventListener('ended', () => this.handleEnded());
      this.audio.addEventListener('error', () => this.handleError());
    }

    togglePlayback() {
      if (this.audio.paused || this.audio.ended) {
        if (activeInstance && activeInstance !== this) {
          activeInstance.audio.pause();
        }

        const playPromise = this.audio.play();

        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            this.updateUi();
          });
        }

        return;
      }

      this.audio.pause();
    }

    handleSliderInput() {
      if (!Number.isFinite(this.audio.duration) || this.audio.duration <= 0) {
        return;
      }

      this.audio.currentTime = Number(this.slider.value);
      this.updateUi();
    }

    handlePlay() {
      if (activeInstance && activeInstance !== this) {
        activeInstance.audio.pause();
      }

      activeInstance = this;
      this.updateUi();
    }

    handlePause() {
      if (activeInstance === this) {
        activeInstance = null;
      }

      this.updateUi();
    }

    handleEnded() {
      if (activeInstance === this) {
        activeInstance = null;
      }

      this.audio.currentTime = 0;
      this.updateUi();
    }

    handleError() {
      this.element.classList.add('is-error');
      this.toggle.disabled = true;
      this.slider.disabled = true;
      this.updateButtonState(false);
    }

    updateUi() {
      const duration = Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
      const currentTime = Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0;
      const progress = duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0;

      this.element.style.setProperty('--qk-sound-progress', `${progress}%`);
      this.element.classList.toggle('is-active', !this.audio.paused && !this.audio.ended);
      this.slider.max = duration > 0 ? String(duration) : '100';
      this.slider.value = duration > 0 ? String(currentTime) : '0';
      this.slider.setAttribute('aria-valuetext', formatTime(currentTime));
      this.currentTimeElement.textContent = formatTime(currentTime);
      this.durationElement.textContent = formatTime(duration);
      this.updateButtonState(!this.audio.paused && !this.audio.ended);
    }

    updateButtonState(isPlaying) {
      this.toggle.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      this.toggle.setAttribute('aria-label', `${isPlaying ? 'Pauza' : 'Reda'} ${this.title}`);
    }
  }

  function initSoundTests(root = document) {
    root.querySelectorAll(SELECTORS.item).forEach((element) => {
      if (element.dataset.qkSoundInitialized === 'true') {
        return;
      }

      new SoundTestTrack(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSoundTests());
  } else {
    initSoundTests();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSoundTests(event.target);
  });
})();
